import { Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';

import { ApiService } from 'src/app/services/api.service';
import { urlValidator } from 'src/app/shared/validators/url-validator';
import { ImageValidatorHelperService } from 'src/app/services/image-validator-helper.service';
import { ImageFile } from 'src/app/core/model/common/ImageFile.interface';
import {
  AD_DESCRIPTION_MAX_LENGTH,
  AD_IMAGE_MAX_BYTES,
  AD_IMAGE_RULES,
  AD_PLACEMENT_OPTIONS,
  AD_SCREEN_OPTIONS,
  AD_TYPE_CODE,
  AdOption,
  AdType,
} from 'src/app/lib/constants/ads.constants';

export interface CreateEditAdsDialogData {
  mode: 'create' | 'edit';
  uid?: number;
  adType?: AdType;
}

type AdFormField =
  | 'title' | 'placement' | 'description' | 'image'
  | 'dateFrom' | 'dateTo' | 'link' | 'screen' | 'duration';

@Component({
  selector: 'app-create-edit-ads-modal',
  templateUrl: './create-edit-ads-modal.component.html',
  styleUrl: './create-edit-ads-modal.component.scss',
})
export class CreateEditAdsModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private fetchedItem: any = null;
  
  form!: UntypedFormGroup;
  isEditMode = false;
  isSubmitting = false;

  // create mode: from the active dashboard tab; edit mode: from the fetched item
  adType: AdType = 'link';

  // ─── Dropdown options ─────────────────────────────────────────────────────
  placementOptions = AD_PLACEMENT_OPTIONS;
  screenOptions = AD_SCREEN_OPTIONS;
  screen: string | null = null;
  screen_text = '';

  // ─── Description ──────────────────────────────────────────────────────────
  descriptionMaxLength = AD_DESCRIPTION_MAX_LENGTH;

  // ─── Image ────────────────────────────────────────────────────────────────
  featured_img: string | null = null;
  imageFile: ImageFile | null = null;
  adImageRules = AD_IMAGE_RULES;
  acceptedImageTypes = AD_IMAGE_RULES.accept;
  isDragging = false;

  // ─── Date validation ──────────────────────────────────────────────────────
  minDateTo: Date | null = null;
  
  // ─── Inline validation ────────────────────────────────────────────────────
  errors: Partial<Record<AdFormField, string>> = {};

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreateEditAdsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateEditAdsDialogData,
    private apiService: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private imageValidator: ImageValidatorHelperService,
    private host: ElementRef<HTMLElement>,
  ) {}

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === 'edit';
    this.adType = this.data?.adType ?? 'link';

    this.buildForm();
    this.watchDateFrom();

    if (this.isEditMode && this.data.uid) {
      // handles its own errors, so no .catch needed here
      void this.fetchItemThenPatch(this.data.uid);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Form setup ───────────────────────────────────────────────────────────

  private buildForm(): void {
    this.form = this.fb.group({
      title: ['', Validators.required],
      placement: ['', Validators.required],
      description: ['', [Validators.required, Validators.maxLength(AD_DESCRIPTION_MAX_LENGTH)]],
      dateFrom: ['', Validators.required],
      dateTo: [{ value: '', disabled: true }, Validators.required],
      duration: ['', [Validators.required, Validators.min(1)]],
      link: ['', this.adType === 'link' ? [Validators.required, urlValidator] : []],
    });
    this.clearErrorOnEdit();
  }

  private watchDateFrom(): void {
    this.form.get('dateFrom')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((dateFrom) => {
        const dateToControl = this.form.get('dateTo')!;

        if (!dateFrom) {
          dateToControl.disable();
          dateToControl.reset();
          this.minDateTo = null;
          return;
        }

        dateToControl.enable();
        this.minDateTo = new Date(dateFrom);

        const currentDateTo = dateToControl.value;
        if (currentDateTo && new Date(currentDateTo) < new Date(dateFrom)) {
          dateToControl.reset();
          this.toastr.warning('Date To has been cleared as it was earlier than Date From', 'Date Updated');
        }
      });
  }

  // ─── Getters ──────────────────────────────────────────────────────────────

  get title(): string {
    return this.isEditMode ? 'Edit Ads' : 'Create Ads';
  }

  get placementText(): string {
    return this.getLabelFromOptions(this.placementOptions, this.form?.get('placement')?.value);
  }

  get previewAspectRatio(): string {
    return `500/500`; // 1:1 aspect ratio
  }

  // Edit mode has no File object, only the URL, so fall back to the URL's file name
  get imageName(): string {
    if (this.imageFile?.name) return this.imageFile.name;
    if (this.featured_img && !this.featured_img.startsWith('data:')) {
      const last = this.featured_img.split('?')[0].split('/').pop() ?? '';
      try { return decodeURIComponent(last); } catch { return last; }
    }
    return '';
  }

  private getLabelFromOptions(options: AdOption[], value: any): string {
    return options.find((o) => String(o.value) === String(value))?.label ?? '';
  }

  // ─── Fetch + patch (edit mode) ────────────────────────────────────────────

  private async fetchItemThenPatch(uid: number): Promise<void> {
    this.spinner.show().catch(console.error);
    try {
      const response = await firstValueFrom(this.apiService.getAdsById(uid));
      this.fetchedItem = response.result;
      this.patchEditValues(this.fetchedItem);
    } catch (error: any) {
      this.toastr.error(error.error?.message || 'Failed to load ad details.', 'Error!');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }

  private patchEditValues(item: any): void {
    this.adType = item.adsType === AD_TYPE_CODE.screen ? 'screen' : 'link';

    const linkControl = this.form.get('link')!;
    if (this.adType === 'link') {
      linkControl.setValidators([Validators.required, urlValidator]);
    } else {
      linkControl.clearValidators();
    }
    linkControl.updateValueAndValidity();

    this.form.patchValue({
      title: item.title,
      placement: item.placement ?? '',
      description: item.description,
      dateFrom: item.dateFrom,
      dateTo: item.dateTo,
      duration: item.duration,
      link: this.adType === 'link' ? item.link : '',
    });
    this.form.get('dateTo')?.enable();
    this.minDateTo = item.dateFrom ? new Date(item.dateFrom) : null;

    if (this.adType === 'screen') {
      this.screen = item.screen;
      this.screen_text = this.getLabelFromOptions(this.screenOptions, item.screen);
    }

    this.featured_img = item.imageUrl || null;
  }

  // ─── Dropdown handlers ────────────────────────────────────────────────────

  setPlacement(value: string): void {
    this.form.get('placement')!.setValue(value);
  }

  setScreen(text: string, value: string): void {
    this.screen = value;
    this.screen_text = text;
    this.clearError('screen');
  }

  // ─── Image: browse + drag & drop ──────────────────────────────────────────

  onSelectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // lets the same file be re-selected
    if (file) this.processImage(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault(); // required, otherwise the browser rejects the drop
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // ignore leave events fired when moving over child elements (button, text)
    const zone = event.currentTarget as HTMLElement;
    if (event.relatedTarget && zone.contains(event.relatedTarget as Node)) return;
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault(); // stops the browser from opening the file
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      this.toastr.warning('Only one image can be uploaded. Using the first file.', 'Multiple Files');
    }
    this.processImage(files[0]);
  }

  // Shared by browse + drop
  private async processImage(file: File): Promise<void> {
    const result = await this.imageValidator.processFileSelection(
      file,
      AD_IMAGE_MAX_BYTES,
      undefined,
      undefined,
      {
        minWidth: AD_IMAGE_RULES.minWidth,
        maxWidth: AD_IMAGE_RULES.maxWidth,
        minHeight: AD_IMAGE_RULES.minHeight,
        maxHeight: AD_IMAGE_RULES.maxHeight,
      }
    );

    if (!result) return; // service already showed the error toast; current preview stays

    this.imageFile = result;
    this.featured_img = result.link;
    this.clearError('image');
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  onDateToContainerClick(): void {
    if (!this.form.get('dateFrom')!.value) {
      this.errors.dateFrom = 'Please select Date From first';
    }
  }

  private clearErrorOnEdit(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)!.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.clearError(key as AdFormField));
    });
  }

  private clearError(field: AdFormField): void {
    if (this.errors[field]) delete this.errors[field];
  }

  private isFormValid(): boolean {
    const { title, placement, description, dateFrom, dateTo, duration, link } = this.form.getRawValue();
    const e: Partial<Record<AdFormField, string>> = {};

    if (!title?.trim()) e.title = 'Title is required';
    if (!placement) e.placement = 'Please select a placement';
    if (!description?.trim()) e.description = 'Details is required';
    if (!duration || isNaN(Number(duration)) || Number(duration) <= 0) {
      e.duration = 'Duration must be a positive number';
    }

    if (this.adType === 'link') {
      if (!link?.trim()) e.link = 'Link is required';
      else if (this.form.get('link')!.hasError('invalidUrl')) {
        e.link = 'Please enter a valid URL (must start with http:// or https://)';
      }
    } else if (!this.screen) {
      e.screen = 'Please select a screen to redirect to';
    }

    if (!this.isEditMode && !this.imageFile?.file) e.image = 'Please select an image';

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    from?.setHours(0, 0, 0, 0);
    to?.setHours(0, 0, 0, 0);

    if (!from) {
      e.dateFrom = 'Date From is required';
    } else if (!to) {
      e.dateTo = 'Date To is required';
    } else if (to < from) {
      e.dateTo = 'Date To must be equal to or after Date From';
    }
    else if (to < today) e.dateTo = 'Date To cannot be in the past';
    else if (from && to < from) e.dateTo = 'Date To must be equal to or after Date From';

    this.errors = e;

    if (Object.keys(e).length) {
      // modal body scrolls, so bring the first error into view
      setTimeout(() =>
        this.host.nativeElement.querySelector('.has-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );
      return false;
    }
    return true;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async submit(): Promise<void> {
    if (!this.isFormValid() || this.isSubmitting) return;

    this.isSubmitting = true;
    this.spinner.show().catch(console.error);

    const raw = this.form.getRawValue();

    try {
      const formData = new FormData();
      if (this.imageFile?.file) formData.append('image', this.imageFile.file);
      formData.append('title', raw.title);
      formData.append('placement', raw.placement);
      formData.append('description', raw.description);
      formData.append('dateFrom', moment(raw.dateFrom).format('YYYY-MM-DD HH:mm:ss'));
      formData.append('dateTo', moment(raw.dateTo).format('YYYY-MM-DD HH:mm:ss'));
      formData.append('duration', raw.duration);
      formData.append('adsType', AD_TYPE_CODE[this.adType]);

      if (this.adType === 'link') {
        formData.append('link', raw.link);
        formData.append('screen', '0');
      } else {
        formData.append('screen', this.screen as string);
        formData.append('link', '0');
      }

      const response: any = this.isEditMode && this.fetchedItem?.uid
        ? await this.apiService.updateAds(formData, this.fetchedItem.uid)
        : await this.apiService.storeAds(formData);

      if (response.status === 200 || response.status === 201) {
        this.toastr.success(
          response.message || (this.isEditMode ? 'Ad updated successfully' : 'Ad created successfully'),
          'Success!'
        );
      } else {
        this.toastr.error(response.message || 'Something went wrong!', 'Error!');
      }
    } catch (error: any) {
      this.toastr.error(error.error?.message || 'Something went wrong!', 'Error!');
    } finally {
      this.dialogRef.close(true);
      this.isSubmitting = false;
      this.spinner.hide().catch(console.error);
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
